import { Briefcase, Mail, Phone, User as UserIcon, UserCheck } from 'lucide-react';
import { ONBOARDING_STATUS_LABELS, type OnboardingCase, type OnboardingStatus } from '../types/onboarding';

interface CandidateSummaryProps {
  candidate: OnboardingCase;
  /** Optional override — when the workflow API is the source of truth for state. */
  liveState?: string;
}

const STATUS_BADGE_CLASS: Partial<Record<OnboardingStatus, string>> = {
  onboarding_start: 'badge-info',
  review_phase: 'badge-warning',
  rejected: 'badge-error',
  onboarding_complete: 'badge-success',
};

const labelForState = (state: string): string =>
  ONBOARDING_STATUS_LABELS[state as OnboardingStatus] ?? state;

const badgeForState = (state: string): string =>
  STATUS_BADGE_CLASS[state as OnboardingStatus] ?? 'badge-info';

interface SummaryRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

const SummaryRow = ({ icon, label, value }: SummaryRowProps) => (
  <div className="flex items-start gap-3">
    <span className="text-rgf-gray-400 mt-0.5">{icon}</span>
    <div className="min-w-0 flex-1">
      <div className="text-xs text-rgf-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-sm text-rgf-gray-900 truncate">{value || '—'}</div>
    </div>
  </div>
);

export const CandidateSummary = ({ candidate, liveState }: CandidateSummaryProps) => {
  const state = liveState || candidate.state;
  const job = candidate.job;

  return (
    <div className="card">
      <div className="card-body space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs text-rgf-gray-500 uppercase tracking-wide">Case #{candidate.id}</div>
            <h2 className="text-xl font-semibold text-rgf-gray-900 truncate">
              {candidate.contact?.name || 'Unnamed Candidate'}
            </h2>
          </div>
          <span className={`badge ${badgeForState(state)} self-start`}>{labelForState(state)}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SummaryRow icon={<Mail className="h-4 w-4" />} label="Email" value={candidate.contact?.email} />
          <SummaryRow icon={<Phone className="h-4 w-4" />} label="Phone" value={candidate.contact?.phone_number} />
          <SummaryRow
            icon={<Briefcase className="h-4 w-4" />}
            label="Job"
            value={
              job
                ? `${job.title || '—'}${job.program ? ` • ${job.program}` : ''}${job.department ? ` • ${job.department}` : ''}`
                : '—'
            }
          />
          <SummaryRow
            icon={<UserIcon className="h-4 w-4" />}
            label="Reporting Manager"
            value={
              // Prefer the resolved display name; fall back to the email
              // when name isn't available (legacy data or unresolved users).
              candidate.reporting_manager_name?.trim() || candidate.reporting_manager
            }
          />
          <SummaryRow
            icon={<UserCheck className="h-4 w-4" />}
            label="Program Associate"
            value={candidate.assigned_program_associate}
          />
          <SummaryRow
            icon={<UserCheck className="h-4 w-4" />}
            label="PNC Officer"
            value={candidate.assigned_pnc_officer}
          />
        </div>

        {candidate.rejection_reason && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <div className="font-medium mb-1">Rejection reason</div>
            <div>{candidate.rejection_reason}</div>
          </div>
        )}
      </div>
    </div>
  );
};
